/* eslint-disable object-curly-newline, arrow-body-style, import/no-extraneous-dependencies */
import { cloneElement } from 'react'
import { Grid } from '@mui/material'
import { useMemoRef, useStyles } from '@platform/react/hook'
import { getThemeColor, isObj, setKey } from 'lib/util'
import Text from 'ui/Element/Text'
import { decorate, decorateValue, useContentDecorators } from 'ui/Element/Text/Decorated'

/**
 * Returns a copy of the string passed as first parameter with the first letter in capital form.
 *
 * @param {string} first    the first character of the string
 * @param {string[]} rest   the rest of the string
 * @param {string} locale   the locale used to convert the first part of the string to uppercase
 * @returns {string}
 */
export const capitalizeFirstLetter = ([first, ...rest], locale = navigator.language) => first
  .toLocaleUpperCase(locale) + rest.join('')

/**
 * Creates a copy of `Component` with props `props` through React's
 * {@link cloneElement}.
 *
 * @param {React.ReactHTMLElement} Component  the component to clone
 * @returns {function(*): React.DetailedReactHTMLElement<*, HTMLElement>}
 */
const clone = Component => props => cloneElement(Component, { ...props, ...Component.props })

/**
 * Traverses the `children` array in search of 'type' properties. Returns an object the name
 * of whose properties is the value of 'type' for each object and its value is the object itself.
 *
 * @param {Object[]} children the children to map
 * @returns {Object}
 */
const childrenTypesObject = children => children
  .filter(child => child.props.type)
  .reduce((types, child) => {
    setKey(clone(child), capitalizeFirstLetter(child.props.type), types)
    return types
  }, {})

/**
 * Returns the LabeledText element's current theme styles.
 *
 * @param {Object} theme        the application's current theme
 * @param {boolean} row         whether to display the items in a row
 * @param {string} [background] optional background color of the paper component
 * @param {string} [color]      optional text color for the label
 * @param {string} [align]      how to align the items if `row` is truthy
 * @returns {Object}            the LabeledText component's styles
 */
const styles = (theme, { row, background, color, align }) => ({
  root: {
    background: background && `${background}!important`,
    display: 'flex',
    ...row ? {
      flex: 1,
      flexDirection: 'row',
      justifyContent: align || 'space-between',
    } : {
      flexDirection: 'column',
    },
  },
  label: {
    fontWeight: 500,
    color: color && getThemeColor(theme, color),
  },
  value: {
    color: theme.palette.black.main,
    fontWeight: 700,
  },
  uppercase: {
    textTransform: 'uppercase',
  },
  center: {
    justifyContent: 'center',
    textAlign: 'center',
  },
  medium: {
    fontSize: theme.typography.pxToRem(theme.typography.fontSize),
  },
  error: {
    color: theme.palette.error.main,
  },
  ...theme.custom.labeledText,
})

/**
 * LabeledText UI element.
 *
 * Two {@link Text} elements, representing a pair Label - Value.
 *
 * @param {Object} forwardedRef       forwarded reference to the root element
 * @param {string} value              the string to set as value
 * @param {string} label              the string to set as label
 * @param {Object[]} children         element's contents
 * @param {Object} props              additional props
 * @param {String} [props.color]      optional color for the label. Default is `secondary`
 * @param {String} [props.background] optional background color for the label
 * @param {String} [props.decorators] optional object whose properties have names of styles to apply
 * @param {Object} [props.classes]    classes to apply to the component
 * @param {Object} [props.autoHide]   whether the element should not be displayed if it has no value
 * @param {boolean} [props.hidden]    whether the element should not be displayed
 * @param {boolean} [props.separator] a separator character to use if `row` is truthy
 * @param {boolean} [props.align]     how to align the elements if `row` is truthy
 * @returns {JSX.Element}
 */
const LabeledText = ({ forwardedRef, value, label, children, ...props }) => {
  const {
    color = {},
    row,
    itemsGap,
    background,
    decorators,
    autoHide = true,
    variant = {},
    separator = null,
    align = null,
  } = props

  const { label: labelDecorators = {}, value: valueDecorators = {} } = decorators || {}
  const { Label = Text, Value = Text } = childrenTypesObject(children)
  const colorIsObj = isObj(color)

  const innerClasses = useStyles(styles, {
    row,
    background,
    align,
    ...!colorIsObj ? color : {},
  })()

  const classes = { ...innerClasses, ...isObj(props.classes) ? props.classes : {} }
  const contentDecorators = useContentDecorators()

  // filling classNames by mapping decorators to css properties
  const labelClasses = decorate(classes, labelDecorators)
  const valueClasses = decorate(classes, valueDecorators)

  // decoration of internal value parts
  const labelText = decorateValue(contentDecorators, labelDecorators, label)
  const valueText = decorateValue(contentDecorators, valueDecorators, value)

  return (!value && autoHide) || props.hidden ? null : (
    <Grid
      ref={forwardedRef}
      item
      container
      className={classes.root}
      gap={itemsGap}
    >
      <Label
        value={labelText}
        variant={variant.label}
        color={color.label}
        className={`${!variant.label ? classes.label : ''}${labelClasses}`}
      />
      {row && separator && (
        <Text value={separator}/>
      )}
      <Value
        value={valueText}
        variant={variant.value}
        color={color.value}
        className={`${!variant.value ? classes.value : ''}${valueClasses}`}
      />
    </Grid>
  )
}

export default useMemoRef(LabeledText, props => [props.value, props.children])
